import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableEmpty, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { apiMessage, runStatusTone, useAutoRefresh } from '../lib';
import { downloadRunArtifact, saveBlob, useReportRuns, useSavedReports } from '../reportingApi';
import type { ReportRun } from '../reporting-types';

const RUNS_PAGE_SIZE = 25;

/**
 * The audit trail of what actually ran, for whom, and whether it arrived —
 * including a run that failed, with the reason. A `FAILED` or
 * `DELIVERED_NONE` row is never rendered the same as a `SUCCEEDED` one with a
 * quiet total; each carries its own error and its own badge colour, so a run
 * that produced nothing is never mistaken for a run that produced zero.
 */
export default function RunsTab() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const { reports } = useSavedReports({ limit: 100 });

  const [savedReportId, setSavedReportId] = useState('');
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState<string | null>(null);

  const { runs, total, loading, error, reload } = useReportRuns({
    page,
    limit: RUNS_PAGE_SIZE,
    savedReportId: savedReportId || undefined,
  });

  const pages = Math.max(1, Math.ceil(total / RUNS_PAGE_SIZE));
  const reportName = useMemo(() => new Map(reports.map((r) => [r.id, r.name])), [reports]);

  // No VENUE_EVENTS exist for a run finishing — see `useAutoRefresh`'s own
  // doc comment. Only the first page polls: a run just landed is exactly what
  // page one would show, and refreshing page 4 under someone's feet would
  // shift the rows they are reading.
  useAutoRefresh(reload, { enabled: page === 1, intervalMs: 20_000 });

  const download = async (run: ReportRun) => {
    if (!can('reports.export')) return;
    setDownloading(run.id);
    try {
      const file = await downloadRunArtifact(run.id, run.artifact?.filename ?? 'report');
      saveBlob(file);
    } catch (err) {
      toast.error(apiMessage(err, t('reporting.runs.downloadFailed')));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Field label={t('reporting.saved.title')}>
            <Select
              value={savedReportId}
              onChange={(event) => {
                setSavedReportId(event.target.value);
                setPage(1);
              }}
            >
              <option value="">{t('reporting.runs.allReports')}</option>
              {reports.map((report) => (
                <option key={report.id} value={report.id}>
                  {report.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </div>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : !runs.length ? (
        <EmptyState
          title={t('reporting.runs.empty')}
          hint={savedReportId ? t('reporting.runs.emptyFilteredHint') : t('reporting.runs.emptyHint')}
        />
      ) : (
        <>
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('reporting.runs.requestedAt')}</TH>
                  <TH>{t('reporting.saved.title')}</TH>
                  <TH>{t('reporting.runs.trigger')}</TH>
                  <TH>{t('common.status')}</TH>
                  <TH className="text-end">{t('reporting.runs.rows')}</TH>
                  <TH className="text-end">{t('common.actions')}</TH>
                </TR>
              </THead>
              <tbody>
                {runs.map((run) => (
                  <TR key={run.id}>
                    <TD className="tabular-nums">{formatDateTime(run.requestedAt)}</TD>
                    <TD className="text-ink-muted">
                      {run.savedReportId ? (reportName.get(run.savedReportId) ?? run.savedReportId) : '—'}
                    </TD>
                    <TD>{t(`reporting.trigger.${run.trigger}`)}</TD>
                    <TD>
                      <div className="space-y-1">
                        <Badge tone={runStatusTone(run.status)}>
                          {t(`reporting.runStatus.${run.status}`)}
                        </Badge>
                        {run.status === 'FAILED' && run.error && (
                          <p className="max-w-sm text-xs text-red-600 dark:text-red-400">
                            {run.errorCode ? `${run.errorCode}: ` : ''}
                            {run.error}
                          </p>
                        )}
                        {run.status === 'DELIVERED_NONE' && (
                          <p className="max-w-sm text-xs text-amber-700 dark:text-amber-400">
                            {t('reporting.runs.deliveredNoneHint')}
                          </p>
                        )}
                      </div>
                    </TD>
                    <TD className="text-end tabular-nums">
                      {run.rowCount === null ? (
                        '—'
                      ) : (
                        <>
                          {run.rowCount}
                          {run.truncated && (
                            <span className="ms-1 text-amber-700 dark:text-amber-400">
                              {t('reporting.runs.truncatedMark')}
                            </span>
                          )}
                        </>
                      )}
                    </TD>
                    <TD className="text-end">
                      {can('reports.export') && run.artifact ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={downloading === run.id}
                          onClick={() => download(run)}
                        >
                          <Icon as={Download} size="sm" aria-hidden />
                          {t('reporting.runs.download')}
                        </Button>
                      ) : (
                        <span className="text-xs text-ink-muted">—</span>
                      )}
                    </TD>
                  </TR>
                ))}
                {!runs.length && <TableEmpty colSpan={6}>{t('reporting.runs.empty')}</TableEmpty>}
              </tbody>
            </Table>
          </TableWrap>

          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-ink-muted">{t('reporting.runs.count', { count: total })}</span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('common.back')}
              </Button>
              <span className="tabular-nums">{t('reporting.runs.pageOf', { page, pages })}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
